import { FC, FocusEvent, useEffect, useRef, useCallback } from 'react';
import { BlockNoteEditor as CoreBlockNoteEditor } from '@blocknote/core';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import { en } from '@blocknote/core/locales';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { blue, grey } from '@mui/material/colors';
import '@blocknote/mantine/style.css';

import fileToDataUrl from './helpers/file-to-data-url';
import getBlockNoteImageUploadError from './helpers/get-block-note-image-upload-error';
import registerRejectUnsupportedImageFileInsert from './helpers/register-reject-unsupported-image-file-insert';
import ensureParagraphAfterBlockAndFocus from './helpers/ensure-paragraph-after-block-and-focus';
import scheduleEnsureParagraphAfterImageUpload from './helpers/schedule-ensure-paragraph-after-image-upload';
import scheduleSetImagePreviewDimensionsAfterUpload from './helpers/schedule-set-image-preview-dimensions-after-upload';
import imagePreviewDimensionsNeedSync from './helpers/image-preview-dimensions-need-sync';
import registerEnsureParagraphAfterImageFileInsert from './helpers/register-ensure-paragraph-after-image-file-insert';
import scheduleEditorLayoutSyncFromDom from './helpers/schedule-editor-layout-sync-from-dom';
import shouldSyncParentIntoEditor from './helpers/should-sync-parent-into-editor';
import stripTrailingEmptyParagraphs from './helpers/strip-trailing-empty-paragraphs';
import emitPreparedDocument from './helpers/emit-prepared-document';
import {
  prepareDocumentForPersist,
  tableBlockNeedsWidthSync,
} from './helpers/sync-table-column-widths-in-document';
import { MUIFormattingToolbar } from './components/MUIFormattingToolbar';
import {
  clearTableCellSelectionIfPresent,
  disableTableCellSelectionInReadonlyExtension,
} from './helpers/disable-table-cell-selection-in-readonly';
import { insertParagraphBetweenAdjacentMediaOnEnterExtension } from './helpers/insert-paragraph-between-adjacent-media-on-enter-extension';
import { schema, BlockNoteDocument, BlockSchema } from './schema';
import './editor.css';

const LINE_HEIGHT = 25;
const DEFAULT_LINES = 5;

export type FlushBlockNoteDocument = () => BlockNoteDocument;

export type RegisterFlushBlockNoteDocument = (
  flush: FlushBlockNoteDocument | null,
) => void;

const EditorContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'extraLines' && prop !== 'editable',
})<{ extraLines: number; editable: boolean }>(
  ({ theme, extraLines, editable }) => ({
    border: editable ? `1px solid ${theme.palette.divider}` : 'none',
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    cursor: editable ? 'text' : 'default',
    padding: editable
      ? theme.spacing(
          theme.spacingSizes.xs,
          theme.spacingSizes.sm,
          theme.spacingSizes.sm,
          theme.spacingSizes.sm,
        )
      : 0,
    '.bn-editor': {
      minHeight: editable
        ? extraLines > 0
          ? (extraLines + DEFAULT_LINES) * LINE_HEIGHT
          : DEFAULT_LINES * LINE_HEIGHT
        : 'auto',
      minWidth: 0,
      maxWidth: '100%',
      cursor: editable ? 'text' : 'default',
      color: grey[900],
    },
    '.bn-container': {
      minWidth: 0,
      maxWidth: '100%',
    },
    '.bn-editor .bn-block-content[data-content-type="image"]': {
      maxWidth: '100%',
    },
    ...(editable
      ? {
          '.bn-editor a': {
            pointerEvents: 'none',
            cursor: 'text',
            color: blue[500],
          },
        }
      : {
          '.bn-editor .bn-block-content[data-content-type="paragraph"]': {
            cursor: 'text',
          },
          '.bn-editor .bn-block-content[data-content-type="paragraph"] .bn-inline-content':
            {
              cursor: 'text',
            },
          '.bn-editor a': {
            pointerEvents: 'auto',
            cursor: 'pointer',
            color: blue[500],
          },
        }),
  }),
);

type Props = {
  value?: BlockNoteDocument;
  onChange?: (document: BlockNoteDocument) => void;
  onError?: (message: string) => void;
  onRegisterFlush?: RegisterFlushBlockNoteDocument;
  placeholder?: string;
  extraLines?: number;
  readOnly?: boolean;
  showTableInToolbar?: boolean;
};

const BlockNoteEditor: FC<Props> = ({
  value,
  onChange,
  onError,
  onRegisterFlush,
  placeholder,
  extraLines = 0,
  readOnly = false,
  showTableInToolbar = true,
}) => {
  const editable = !readOnly;
  const showTableHandles = editable ? showTableInToolbar : false;
  const parsedValue =
    Array.isArray(value) && value.length > 0 ? value : undefined;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const onRegisterFlushRef = useRef(onRegisterFlush);
  onRegisterFlushRef.current = onRegisterFlush;

  const editorRef = useRef<CoreBlockNoteEditor<BlockSchema> | null>(null);

  const notifyEditorError = useCallback((message: string) => {
    const onError = onErrorRef.current;
    if (onError) {
      window.setTimeout(() => {
        onError(message);
      }, 0);
    }
  }, []);

  const uploadFile = useCallback(
    async (file: File, blockId?: string) => {
      const message = getBlockNoteImageUploadError(file);
      if (message) {
        notifyEditorError(message);
        if (blockId && editorRef.current) {
          const block = editorRef.current.getBlock(blockId);
          if (block) {
            editorRef.current.removeBlocks([block]);
          }
        }
        return Promise.reject(new Error(message));
      }
      const result = await fileToDataUrl(file);
      const editor = editorRef.current;
      if (editor) {
        scheduleSetImagePreviewDimensionsAfterUpload(editor, blockId, file);
        scheduleEnsureParagraphAfterImageUpload(editor, blockId, result);
      }
      return result;
    },
    [notifyEditorError],
  );

  const editor = useCreateBlockNote(
    {
      schema,
      extensions: [
        insertParagraphBetweenAdjacentMediaOnEnterExtension(),
        disableTableCellSelectionInReadonlyExtension(),
      ],
      uploadFile,
      dictionary: {
        ...en,
        placeholders: {
          ...en.placeholders,
          default: '',
          emptyDocument: placeholder ?? '',
        },
      },
      initialContent: parsedValue,
    },
    [uploadFile, placeholder],
  );
  editorRef.current = editor;

  const editorValueRef = useRef<string>(JSON.stringify(parsedValue ?? null));

  const flushDocumentRef = useRef<FlushBlockNoteDocument>(() => []);
  flushDocumentRef.current = () => {
    const prepared = prepareDocumentForPersist(editor, [...editor.document], {
      measureAllTableWidthsFromDom: !!editor.domElement,
    });
    const preparedStr = JSON.stringify(prepared);
    editorValueRef.current = preparedStr;
    onChangeRef.current?.(prepared);
    return prepared;
  };

  useEffect(() => {
    const register = onRegisterFlushRef.current;
    if (!register) {
      return;
    }
    register(() => flushDocumentRef.current());
    return () => {
      register(null);
    };
  }, [onRegisterFlush]);

  const prevEditableRef = useRef(editable);

  useEffect(() => {
    const transitioningToReadonly = prevEditableRef.current && !editable;
    prevEditableRef.current = editable;

    if (transitioningToReadonly) {
      clearTableCellSelectionIfPresent(editor);
      const prepared = prepareDocumentForPersist(editor, [...editor.document], {
        measureAllTableWidthsFromDom: true,
      });
      const preparedStr = JSON.stringify(prepared);
      editorValueRef.current = preparedStr;
      onChangeRef.current?.(prepared);
      if (prepared.length > 0) {
        editor.replaceBlocks(editor.document, prepared);
      }
      const firstBlock = editor.document[0];
      if (firstBlock) {
        editor.setTextCursorPosition(firstBlock, 'start');
      }
      editor.domElement?.blur();
      return;
    }

    const newStr = JSON.stringify(parsedValue ?? null);
    const hasExternalChange = newStr !== editorValueRef.current;
    const shouldSync =
      hasExternalChange &&
      shouldSyncParentIntoEditor(editor.document, parsedValue);

    if (shouldSync) {
      editorValueRef.current = newStr;
      if (parsedValue && parsedValue.length > 0) {
        editor.replaceBlocks(editor.document, parsedValue);
      }
    }
  }, [editable, parsedValue, editor]);

  useEffect(() => {
    if (!editable) {
      return;
    }

    const ensureAfterImage = (blockId: string) => {
      setTimeout(() => {
        ensureParagraphAfterBlockAndFocus(editor, blockId);
      }, 0);
    };

    const unregisterDropPaste = registerEnsureParagraphAfterImageFileInsert(
      editor,
      ensureAfterImage,
    );
    const unregisterRejectUnsupported =
      registerRejectUnsupportedImageFileInsert(editor, notifyEditorError);

    return () => {
      unregisterDropPaste();
      unregisterRejectUnsupported();
    };
  }, [editor, editable, notifyEditorError]);

  useEffect(() => {
    if (!editable) {
      return;
    }

    const needsLayoutSync = editor.document.some(
      (block) =>
        tableBlockNeedsWidthSync(block) ||
        imagePreviewDimensionsNeedSync(block),
    );
    if (!needsLayoutSync) {
      return;
    }

    return scheduleEditorLayoutSyncFromDom(editor, () => {
      emitPreparedDocument(
        editor,
        [...editor.document],
        editorValueRef,
        onChange,
      );
    });
  }, [editable, editor, parsedValue, onChange]);

  useEffect(() => {
    return () => {
      const prepared = stripTrailingEmptyParagraphs([...editor.document]);
      onChangeRef.current?.(prepared);
    };
  }, [editor]);

  const handleEditorBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!editable || !onChange) {
      return;
    }

    const nextFocused = event.relatedTarget;
    if (
      nextFocused instanceof Node &&
      event.currentTarget.contains(nextFocused)
    ) {
      return;
    }

    emitPreparedDocument(
      editor,
      [...editor.document],
      editorValueRef,
      onChange,
      { measureAllTableWidthsFromDom: true },
    );
  };

  return (
    <EditorContainer
      extraLines={extraLines}
      editable={editable}
      onBlur={handleEditorBlur}
    >
      <BlockNoteView
        editor={editor}
        formattingToolbar={false}
        slashMenu={false}
        sideMenu={false}
        linkToolbar={false}
        filePanel={false}
        tableHandles={showTableHandles}
        emojiPicker={false}
        onChange={({ document }) => {
          emitPreparedDocument(editor, [...document], editorValueRef, onChange);
        }}
        theme="light"
        style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
        editable={editable}
      >
        {editable ? (
          <MUIFormattingToolbar showTable={showTableInToolbar} />
        ) : null}
      </BlockNoteView>
    </EditorContainer>
  );
};

export default BlockNoteEditor;
